import { updateSocketToken } from './socket';

export const API_URL = '/api';

let accessToken: string | null = null;
let refreshPromise: Promise<any> | null = null;


function reportApiError(endpoint: string, message: string, status?: number) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('clientix:api-error', {
    detail: { endpoint, message, status }
  }));
}

function toNetworkError(error: unknown) {
  if (error instanceof Error && error.message) return error;
  return new Error('Не удалось связаться с сервером. Проверьте подключение и повторите попытку.');
}
function toPublicApiMessage(message: unknown, status: number) {
  const fallback = 'Не удалось выполнить запрос. Повторите попытку.';
  if (status >= 500 || typeof message !== 'string') return fallback;
  const looksTechnical = message.length > 180 || /prisma|invocation|stack|\bat \w+\.tsx?:\d+/i.test(message);
  return looksTechnical ? fallback : message;
}

export function setAccessToken(token: string | null) {
  accessToken = token;
}

async function request(endpoint: string, options: RequestInit, token: string | null) {
  const headers = new Headers(options.headers || {});
  if (options.body && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);

  return fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
    credentials: 'include',
  });
}

export async function refreshSession(): Promise<any> {
  if (!refreshPromise) {
    const performRefresh = async () => {
      const response = await request('/auth/refresh', { method: 'POST' }, null);
      if (!response.ok) throw new Error('Session refresh failed');
      const data = await response.json();
      setAccessToken(data.token || null);
      if (data.token) updateSocketToken(data.token);
      return data;
    };

    const coordinatedRefresh = typeof navigator !== 'undefined' && navigator.locks
      ? navigator.locks.request('clientix-auth-refresh', performRefresh)
      : performRefresh();
    refreshPromise = coordinatedRefresh.finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

async function refreshAccessToken(): Promise<string | null> {
  try {
    const session = await refreshSession();
    return session?.token || null;
  } catch {
    return null;
  }
}

export async function fetchApi(endpoint: string, options: RequestInit = {}) {
  let response: Response;
  try {
    response = await request(endpoint, options, accessToken);
  } catch (error) {
    const networkError = toNetworkError(error);
    reportApiError(endpoint, 'Нет связи с сервером. Проверьте интернет и повторите попытку.');
    throw networkError;
  }

  const mayRefresh = response.status === 401
    && endpoint !== '/auth/login'
    && endpoint !== '/auth/register'
    && endpoint !== '/auth/refresh';

  if (mayRefresh) {
    let refreshedToken: string | null;
    try {
      refreshedToken = await refreshAccessToken();
    } catch (error) {
      const networkError = toNetworkError(error);
      reportApiError(endpoint, 'Не удалось восстановить сессию. Повторите попытку.');
      throw networkError;
    }
    if (refreshedToken) response = await request(endpoint, options, refreshedToken);
  }

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    if (response.status === 401) setAccessToken(null);
    const message = toPublicApiMessage(data.error, response.status);
    // Validation, access-control and conflict responses belong to the component
    // that made the request. Promoting an expected 403 (for example an AGENT
    // probing owner-only team data) to a whole-app outage is misleading.
    if (response.status >= 500) reportApiError(endpoint, message, response.status);
    throw new Error(message);
  }

  return data;
}
